<template>
  <v-menu
    v-model="menu"
    v-bind:close-on-content-click="false"
    location="right bottom"
    origin="right top"
    transition="scale-transition"
    z-index="2"
    v-bind:offset="8"
    eager
  >
    <template #activator="{ props }">
      <slot v-bind="{ props }" name="activator" :hasAppliedFilters="hasAppliedFilters" />
    </template>

    <v-card elevation="2" border width="320">
      <v-card-text class="pt-8">
        <div>
          <v-date-input
            v-model="datesRange"
            :label="$t('menus.topBars.filterByUploadDate')"
            max-width="368"
            multiple="range"
            block
            density="compact"
            flat
            variant="solo-filled"
            :min="getMinDatepicker"
            :max="getMaxDatepicker"
            clearable
            @click:clear="clearSelecedDates"
            @update:modelValue="dateRangeInput"
            prepend-inner-icon="mdi-calendar"
            prepend-icon=""
          />
        </div>

        <div>
          <v-select
            v-model="selectedUploaders"
            :items="getAvailableUploaders"
            :disabled="getAvailableUploaders.length === 0"
            :label="$t('menus.topBars.filterByUploader')"
            item-title="name"
            item-value="id"
            v-on:update:model-value="selectedUploadersChanged"
            multiple density="compact" return-object
            flat variant="solo-filled"
          />
        </div>

        <div>
          <v-select
            v-model="selectedFileTypes"
            :items="getAvailableFileTypes"
            :disabled="getAvailableFileTypes.length === 0"
            :label="$t('menus.topBars.filterByFileType')"
            v-on:update:model-value="selectedFiletypesChanged"
            multiple density="compact" return-object
            flat variant="solo-filled"
          >
            <template v-slot:item="{ props }">
              <v-list-item v-bind="props" tabindex="-1" aria-hidden="true">
                <template v-slot:prepend="{ select, isSelected }">
                  <v-checkbox-btn
                    :model-value="isSelected"
                    @update:model-value="(v) => select(v)"
                    :aria-label="$t('common.toggle')"
                    aria-hidden="true"
                  />
                </template>
              </v-list-item>
            </template>
          </v-select>
        </div>

        <div>
          <v-select
            v-model="selectedPodStatuses"
            :items="podStatusOptions"
            :disabled="podStatusOptions.length === 0"
            :label="$t('menus.topBars.filterByPodStatus')"
            item-title="text"
            item-value="id"
            v-on:update:model-value="podStatusChanged"
            multiple density="compact"
            flat variant="solo-filled"
          />
        </div>

        <div class="text-center">
          <v-btn class="bg-primary" size="small" elevation="0" v-on:click="resetFilters" v-bind:disabled="!hasAppliedFilters">
            {{$t('menus.topBars.resetFilter')}}
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

  </v-menu>
</template>

<script>
import { useNodeStore } from '@/stores/NodeStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import { useFilterStore } from '@/stores/FilterStore.js';
import { formatDate } from '@/components/formatters/timestamp.js';

export default {
  name: "FilterForm",
  data: function() {
    return {
      menu: false,
      datesRange: [],

      selectedUploaders: [],
      selectedFileTypes: [],
      selectedPodStatuses: [],
    };
  },
  beforeUnmount() {
    // Hackermanftw: FilterForm is (almost) always unmounted when navigating between nodes.
    // In the future, maybe consider extracting store mutations in the main node view component
    // and let this one just emit the appropriate events.
    this.resetFilters();
  },
  methods: {
    dateRangeInput: function() {
      const filterStore = useFilterStore();
      filterStore.setDatesRangeFilter(this.datesRange);
    },
    clearSelecedDates: function() {
      this.datesRange = [];
      const filterStore = useFilterStore();
      filterStore.setDatesRangeFilter(this.datesRange);
    },
    selectedUploadersChanged: function() {
      const filterStore = useFilterStore();
      filterStore.setUploadersFilter(this.selectedUploaders);
    },
    selectedFiletypesChanged: function() {
      const filterStore = useFilterStore();
      filterStore.setFiletypesFilter(this.selectedFileTypes);
    },
    podStatusChanged: function() {
      const filterStore = useFilterStore();
      filterStore.setPodStatusFilter(this.selectedPodStatuses);
    },
    resetFilters: function() {
      //this.datesRange = [];
      this.selectedUploaders = [];
      this.selectedFileTypes = [];
      this.selectedPodStatuses = [];

      this.clearSelecedDates();
      this.selectedUploadersChanged();
      this.selectedFiletypesChanged();
      this.podStatusChanged();
    },
  },
  watch: {
    currentNode: function(oldNode, newNode) {
      if (oldNode && oldNode.id && newNode && newNode.id && oldNode.id === newNode.id) {
        return;
      }
      this.resetFilters();
    },
  },
  computed: {
    currentNode: function() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    getList: function() {
      const filterStore = useFilterStore();
      return filterStore.getFilteredList();
    },

    // Date range filter start
    getAvailableDates: function() {
      const filterStore = useFilterStore();
      return filterStore.getAvailableDates;
    },
    getMinDatepicker: function() {
      if (this.getAvailableDates && this.getAvailableDates[0]) {
        return formatDate(new Date(this.getAvailableDates[0]));
      }
      return formatDate(new Date('1900-01-01'));
    },
    getMaxDatepicker: function() {
      if (this.getAvailableDates && this.getAvailableDates[1]) {
        return formatDate(new Date(this.getAvailableDates[1]));
      }
      return formatDate(new Date());
    },
    // Date range filter end


    // Uploaders filter start
    getAvailableUploaders: function() {
      const filterStore = useFilterStore();
      return filterStore.getAvailableUploaders;
    },
    // Uploaders filter ends

    // file types filter start
    getAvailableFileTypes: function() {
      const filterStore = useFilterStore();
      return filterStore.getAvailableFileTypes;
    },
    // file types filter end

    // Pod status filter start
    podBadgeStatuses: function() {
      const commonStore = useCommonStore();
      return commonStore.podBadgeStatuses;
    },
    getAvailablePodStatuses: function() {
      const filterStore = useFilterStore();
      return filterStore.getAvailablePodStatuses;
    },
    podStatusOptions: function() {
      let statuses = [
        {id: this.podBadgeStatuses.NOTSELECTED, text: this.$t('routes.pod.badgeStatusNotSelected')},
        {id: this.podBadgeStatuses.NEW, text: this.$t('routes.pod.badgeStatusNew')},
        {id: this.podBadgeStatuses.DRAFT, text: this.$t('routes.pod.badgeStatusDraft')},
        {id: this.podBadgeStatuses.PRODUCTION, text: this.$t('routes.pod.badgeStatusProduction')},
        {id: this.podBadgeStatuses.REVIEW, text: this.$t('routes.pod.badgeStatusReview')},
        {id: this.podBadgeStatuses.CORRECTION, text: this.$t('routes.pod.badgeStatusCorrection')},
        {id: this.podBadgeStatuses.FINAL, text: this.$t('routes.pod.badgeStatusFinal')},
      ];


      return statuses.filter(x => this.getAvailablePodStatuses.indexOf(x.id) >= 0);
    },
    hasAppliedFilters: function() {
      return this.datesRange.length > 0 ||
        this.selectedUploaders.length > 0 ||
        this.selectedFileTypes.length > 0 ||
        this.selectedPodStatuses.length > 0;
    },
  },
}
</script>
